import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateLayout } from "@/shared/domain/layouts/hooks";
import { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";
import { CloseIcon } from "@/shared/foundation/icons/mui";

import { FolderNameInput } from "./FolderNameInput";
import { useBulkLayoutMutation } from "./useBulkLayoutMutation";

interface MoveLayoutModalProps {
  layouts: LayoutRecord[];
  existingFolders: string[];
  isOpen: boolean;
  onClose: () => void;
  onExited: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

export function MoveLayoutModal({
  layouts,
  existingFolders,
  isOpen,
  onClose,
  onExited,
  onSuccess,
}: MoveLayoutModalProps) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited }}
      aria-labelledby="move-layout-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      {layouts.length > 0 && (
        <MoveLayoutForm
          layouts={layouts}
          existingFolders={existingFolders}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}

interface MoveLayoutFormProps {
  layouts: LayoutRecord[];
  existingFolders: string[];
  onClose: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

function MoveLayoutForm({
  layouts,
  existingFolders,
  onClose,
  onSuccess,
}: MoveLayoutFormProps) {
  // Prefill only when the selection shares one folder; mixed selections start blank.
  const [folder, setFolder] = React.useState(() => {
    const firstFolder = layouts[0]?.folder ?? "";
    return layouts.every((layout) => (layout.folder ?? "") === firstFolder)
      ? firstFolder
      : "";
  });
  const updateLayout = useUpdateLayout();
  const { run, isPending, errorMessage } = useBulkLayoutMutation(
    "Failed to move layouts. Please try again.",
  );

  // Blank/whitespace → null moves layouts to the root (ungrouped).
  const targetFolder = folder.trim() || null;
  const count = layouts.length;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    run(
      layouts,
      (layout) =>
        updateLayout.mutateAsync({
          layoutId: layout.layout_id,
          updates: { folder: targetFolder },
        }),
      () =>
        onSuccess(
          layouts.map((layout) => ({ ...layout, folder: targetFolder })),
        ),
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <DialogTitle id="move-layout-dialog-title" sx={{ pr: 6 }}>
        {count === 1 ? "Move layout to a folder" : `Move ${count} layouts`}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1">
            {count === 1 ? (
              <>
                Choose a folder for <b>&quot;{layouts[0].name}&quot;</b>, or
                leave it blank to keep it ungrouped.
              </>
            ) : (
              <>
                Choose a folder for the <b>{count}</b> selected layouts, or
                leave it blank to keep them ungrouped.
              </>
            )}
          </Typography>

          <FolderNameInput
            value={folder}
            onChange={setFolder}
            existingFolders={existingFolders}
            autoFocus
            disabled={isPending}
          />

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <RobotoButton
          eventName="MoveLayoutsCanceled"
          eventProperties={{ count }}
          onClick={onClose}
          type="button"
          disabled={isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="MoveLayoutsClicked"
          eventProperties={() => ({ count, folder: targetFolder })}
          type="submit"
          variant="contained"
          disabled={isPending}
          endIcon={
            isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Move
        </RobotoButton>
      </DialogActions>
    </form>
  );
}
